import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import {
  formatScheduleWhen,
  isScheduleValid,
  scheduleFromLabel,
  todayIso,
} from '@/utils/bulkPayments';
import type { BulkSchedule, BulkTransfer } from '@/types/portal';

interface RescheduleBatchModalProps {
  transfer: BulkTransfer | null;
  onClose: () => void;
  onConfirm: (transfer: BulkTransfer, schedule: BulkSchedule) => void;
}

const dateInputClass =
  'h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30';

/** ISO date string for "today + n days". */
function isoInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const presets: { label: string; days: number; time: string }[] = [
  { label: 'Tomorrow 9:00', days: 1, time: '09:00' },
  { label: 'Tomorrow 17:00', days: 1, time: '17:00' },
  { label: 'In 3 days', days: 3, time: '09:00' },
  { label: 'In a week', days: 7, time: '09:00' },
];

/**
 * Lets finance move a scheduled batch to a new release day and time straight
 * from the recent-transfers list, without reopening the whole wizard.
 */
export default function RescheduleBatchModal({
  transfer,
  onClose,
  onConfirm,
}: RescheduleBatchModalProps) {
  const [schedule, setSchedule] = useState<BulkSchedule>(() =>
    scheduleFromLabel(transfer?.scheduledFor),
  );

  // Re-seed the picker every time a different batch opens.
  useEffect(() => {
    setSchedule(scheduleFromLabel(transfer?.scheduledFor));
  }, [transfer?.id, transfer?.scheduledFor]);

  const valid = isScheduleValid(schedule);
  const when = formatScheduleWhen(schedule);

  return (
    <Modal
      open={!!transfer}
      onClose={onClose}
      icon="ri-calendar-schedule-line"
      title="Reschedule batch"
      subtitle={transfer ? `${transfer.reference} · ${transfer.recipientCount} recipients` : undefined}
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Keep current time
          </Button>
          <Button
            icon="ri-calendar-check-line"
            disabled={!valid}
            onClick={() => transfer && onConfirm(transfer, schedule)}
          >
            Save new time
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {transfer?.scheduledFor ? (
          <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5 text-xs text-foreground-600">
            <i className="ri-history-line" />
            Currently set to go out {transfer.scheduledFor}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
              Release date
            </span>
            <input
              type="date"
              value={schedule.date}
              min={todayIso()}
              onChange={(event) => setSchedule({ ...schedule, date: event.target.value })}
              className={dateInputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
              Release time
            </span>
            <input
              type="time"
              value={schedule.time}
              onChange={(event) => setSchedule({ ...schedule, time: event.target.value })}
              className={dateInputClass}
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() =>
                setSchedule({ mode: 'scheduled', date: isoInDays(preset.days), time: preset.time })
              }
              className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-[11px] font-label font-medium text-foreground-600 transition-colors hover:border-secondary-300 hover:text-secondary-900"
            >
              <i className="ri-calendar-line" />
              {preset.label}
            </button>
          ))}
        </div>

        <div
          className={`flex items-start gap-2.5 rounded-md border px-3.5 py-3 ${
            valid ? 'border-accent-200 bg-accent-50' : 'border-primary-200 bg-primary-50'
          }`}
        >
          <span
            className={`mt-0.5 flex h-4 w-4 items-center justify-center text-base ${
              valid ? 'text-accent-600' : 'text-primary-600'
            }`}
          >
            <i className={valid ? 'ri-calendar-check-line' : 'ri-error-warning-line'} />
          </span>
          <div>
            <p
              className={`text-xs font-label font-medium ${
                valid ? 'text-accent-900' : 'text-primary-800'
              }`}
            >
              {valid ? 'New release time' : 'Pick a future date and time'}
            </p>
            <p
              className={`mt-0.5 text-xs leading-relaxed ${
                valid ? 'text-accent-800' : 'text-primary-700'
              }`}
            >
              {valid
                ? `This batch will now go out automatically on ${when}, provided the source wallet is funded.`
                : 'Scheduled batches must be set for a future date and time.'}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
}